// CustomNotification.js
import React, { useState, useEffect } from 'react';

// Styles for the notification container
const containerStyle = {
  position: 'fixed',
  top: '16px',
  right: '16px',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxWidth: '350px'
};

// Notification component with different types
const CustomNotification = ({ 
  type = 'info',  // 'success', 'error', 'info', 'warning'
  message = '',
  description = '',
  duration = 4500,
  onClose,
  visible = true
}) => {
  const [isVisible, setIsVisible] = useState(visible);
  
  // Colors based on type
  const getStyles = () => {
    switch(type) {
      case 'success':
        return {
          backgroundColor: '#f6ffed',
          borderColor: '#b7eb8f',
          iconColor: '#52c41a',
          icon: '✓'
        };
      case 'error':
        return {
          backgroundColor: '#fff2f0',
          borderColor: '#ffccc7',
          iconColor: '#ff4d4f',
          icon: '✕'
        };
      case 'warning':
        return {
          backgroundColor: '#fffbe6',
          borderColor: '#ffe58f',
          iconColor: '#faad14',
          icon: '⚠'
        };
      case 'info':
      default:
        return {
          backgroundColor: '#e6f7ff',
          borderColor: '#91d5ff',
          iconColor: '#1890ff',
          icon: 'ℹ'
        };
    }
  };
  
  const styles = getStyles();
  
  // Auto-close notification after duration
  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);
  
  // Handle manual close
  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };
  
  if (!isVisible) return null;
  
  return (
    <div style={{
      padding: '12px 24px 12px 48px',
      borderRadius: '4px',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08)',
      border: `1px solid ${styles.borderColor}`,
      backgroundColor: styles.backgroundColor,
      position: 'relative',
      margin: '8px 0',
      animation: 'fadeIn 0.3s'
    }}>
      {/* Icon */}
      <div style={{
        position: 'absolute',
        left: '16px',
        top: '14px',
        color: styles.iconColor,
        fontSize: '16px',
        fontWeight: 'bold'
      }}>
        {styles.icon}
      </div>
      
      {/* Content */}
      <div>
        {message && (
          <div style={{ 
            fontSize: '16px', 
            lineHeight: '24px', 
            marginBottom: description ? '4px' : '0', 
            fontWeight: '500' 
          }}>
            {message}
          </div>
        )}
        
        {description && (
          <div style={{ 
            fontSize: '14px', 
            color: 'rgba(0, 0, 0, 0.65)' 
          }}>
            {description}
          </div>
        )}
      </div>
      
      {/* Close button */}
      <button 
        onClick={handleClose} 
        style={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          color: 'rgba(0, 0, 0, 0.45)',
          lineHeight: '1'
        }}
      >
        ×
      </button>
      
      {/* Add CSS animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// NotificationProvider component to manage all notifications
const NotificationContext = React.createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  
  // Add a new notification
  const addNotification = (notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
    return id;
  };
  
  // Remove a notification by id
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  
  // Helper functions for different notification types
  const api = {
    success: (config) => addNotification({ type: 'success', ...config }),
    error: (config) => addNotification({ type: 'error', ...config }),
    info: (config) => addNotification({ type: 'info', ...config }),
    warning: (config) => addNotification({ type: 'warning', ...config }),
    open: (config) => addNotification(config),
    remove: (id) => removeNotification(id)
  };
  
  return (
    <NotificationContext.Provider value={api}>
      {children}
      <div style={containerStyle}>
        {notifications.map(notification => (
          <CustomNotification 
            key={notification.id}
            {...notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

// Hook to use notifications in any component
export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default CustomNotification;